import { Chess } from "@vendor/chess.js";
import { positionKey } from "../chess/positionKey.ts";
import { teachingMoveFor } from "../lessons/repository.ts";
import type { LessonCourse } from "../lessons/schema.ts";
import { trainingPrompt } from "./content.ts";
import type { PracticeTask } from "./types.ts";

/** A conservative board question when no independently authored retrieval cue exists. */
function boardCue(fen: string, student: "w" | "b"): string {
  const board = new Chess(fen);
  if (board.inCheck())
    return "Your king is in check. Compare ways to remove the threat before making a plan.";
  const opponent = student === "w" ? "b" : "w";
  const central = student === "w" ? ["d4", "e4"] : ["d5", "e5"];
  if (
    central.some(
      (square) =>
        board.get(square as never)?.color === student && board.isAttacked(square as never, opponent)
    )
  )
    return "Your central pawn is under attack. Compare defending it with a concrete counterattack or an exchange.";
  return "Read the opponent’s last move. Which response fits this position’s central tension, piece development and king safety?";
}

export function taskForPosition(course: LessonCourse, fen: string): PracticeTask | null {
  const key = positionKey(fen);
  const node = course.nodes[key];
  if (!node || node.handoff || fen.split(" ")[1] !== (course.side === "white" ? "w" : "b"))
    return null;
  const move = teachingMoveFor(node);
  if (!move) return null;
  const authored = trainingPrompt(course, key);
  const legal = new Set(new Chess(fen).moves());
  if (!legal.has(move.san)) return null;
  const accepted = node.moves
    .filter((edge) => {
      if (["repertoire", "mainline", "transposition"].includes(edge.role)) return true;
      // A supported taught branch is legitimate; a handoff into another opening is a different task.
      const dest = course.nodes[edge.nextPositionKey];
      return (
        edge.role === "sideline" &&
        dest &&
        !dest.handoff &&
        dest.moves.some((next) => next.role === "mainline")
      );
    })
    .map((edge) => edge.san)
    .filter((san) => legal.has(san));
  return {
    id: `${course.id}:${key}`,
    courseId: course.id,
    openingId: course.openingId,
    contentVersion: course.contentVersion,
    fen,
    positionKey: key,
    student: course.side === "white" ? "w" : "b",
    expected: move.san,
    accepted: [...new Set([move.san, ...accepted])],
    cue: authored?.cue || boardCue(fen, course.side === "white" ? "w" : "b"),
  };
}

export function taskAccepts(task: PracticeTask, san: string): boolean {
  const bare = (value: string) => value.replace(/[+#?!]/g, "");
  return task.accepted.some((accepted) => bare(accepted) === bare(san));
}

/** A legal, authored continuation for feedback only, never an engine guess. */
export function practiceExample(course: LessonCourse, fen: string): string | undefined {
  const board = new Chess(fen);
  const pieces: string[] = [];
  for (let i = 0; i < 4; i += 1) {
    const node = course.nodes[positionKey(board.fen())];
    if (!node || node.handoff) break;
    const edge = teachingMoveFor(node);
    if (!edge || !board.moves().includes(edge.san)) break;
    const number = Number(board.fen().split(" ")[5]);
    const mark = board.turn() === "w" ? `${number}.` : pieces.length ? "" : `${number}…`;
    pieces.push(`${mark}${edge.san}`);
    board.move(edge.san);
  }
  return pieces.length ? pieces.join(" ") : undefined;
}
